/* Progressive enhancement: real next-page links remain usable without JS. */
(() => {
  if (customElements.get('vics-show-more')) return;
  class VicsShowMore extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.grid = this.querySelector('[data-more-grid]');
      this.controls = this.querySelector('[data-more-controls]');
      if (!this.grid || !this.controls) return;
      this.pages = [];
      this.key = location.pathname + location.search;
      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => {
        const link = event.target.closest('[data-more-next]');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button) return;
        event.preventDefault();
        if (!this.busy) this.load(link.href);
      }, options);
      document.addEventListener('click', event => {
        if (event.target.closest('a[href]')) this.remember();
      }, { ...options, capture: true });
      window.addEventListener('pagehide', () => this.remember(), options);
      const saved = history.state?.vicsShowMore?.[this.id];
      if (saved?.key === this.key && Array.isArray(saved.pages) && saved.pages.length) {
        this.restore(saved);
      } else this.progress();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
    remember() {
      if (this.restoring || !this.grid?.isConnected) return;
      const wrapper = document.querySelector('.page-wrapper');
      const state = { key: this.key, pages: [...this.pages], y: window.scrollY, innerY: wrapper?.scrollTop || 0 };
      try {
        history.replaceState({ ...history.state, vicsShowMore: { ...history.state?.vicsShowMore, [this.id]: state } }, '');
      } catch (_) { /* Native browser history still works if state is unavailable. */ }
    }
    progress() {
      const status = this.controls.querySelector('[data-more-status]');
      const total = Number(this.controls.dataset.total);
      const offset = Number(this.dataset.offset || 0);
      const count = [...this.grid.children].filter(n => n.matches('[data-more-item]')).length;
      const end = Math.min(offset + count, total);
      if (status) status.textContent = 'Showing ' + (offset ? (offset + 1) + '–' : '') + end + ' of ' + total + ' ' + (this.controls.dataset.unit || 'items');
    }
    async load(href, restoring = false) {
      if (this.busy) return false;
      const url = new URL(href, location.href);
      if (url.origin !== location.origin || this.pages.includes(url.href)) return false;
      this.busy = true;
      const link = this.controls.querySelector('[data-more-next]');
      if (link) { link.textContent = 'Loading…'; link.setAttribute('aria-disabled', 'true'); }
      this.grid.setAttribute('aria-busy', 'true');
      const error = this.controls.querySelector('[data-more-error]');
      if (error) error.textContent = '';
      const request = new AbortController();
      const timeout = setTimeout(() => request.abort(), 15000);
      const disconnect = () => request.abort();
      this.controller.signal.addEventListener('abort', disconnect, { once: true });
      try {
        const response = await fetch(url.href, { credentials: 'same-origin', signal: request.signal });
        if (!response.ok) throw new Error('Page request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = doc.getElementById(this.id);
        const incomingGrid = incoming?.querySelector('[data-more-grid]');
        const incomingControls = incoming?.querySelector('[data-more-controls]');
        if (!incomingGrid || !incomingControls || !this.isConnected) throw new Error('Missing results');
        const items = [...incomingGrid.children].filter(n => n.matches('[data-more-item]'));
        if (!items.length) throw new Error('No additional results');
        const known = new Set([...this.grid.querySelectorAll('[data-more-key]')].map(n => n.dataset.moreKey));
        const fresh = items.filter(n => !n.dataset.moreKey || !known.has(n.dataset.moreKey));
        if (!fresh.length) throw new Error('No additional results');
        this.grid.append(...fresh);
        const next = incomingControls.querySelector('[data-more-next]');
        if (link && next) { link.href = next.href; link.textContent = 'Show More'; }
        else if (link) link.remove();
        this.pages.push(url.href);
        this.progress();
        if (!restoring) {
          // Place keyboard focus at the beginning of the newly appended batch.
          const focus = fresh[0].querySelector('a[href],button') || fresh[0];
          if (!focus.matches('a,button')) focus.setAttribute('tabindex', '-1');
          focus.focus({ preventScroll: true });
          this.remember();
        }
        return true;
      } catch (_) {
        if (this.isConnected && error) error.textContent = 'Could not load more. Please try again.';
        return false;
      } finally {
        clearTimeout(timeout);
        this.controller?.signal.removeEventListener('abort', disconnect);
        this.busy = false;
        this.grid.removeAttribute('aria-busy');
        if (link?.isConnected) { link.textContent = 'Show More'; link.removeAttribute('aria-disabled'); }
      }
    }
    async restore(saved) {
      this.restoring = true;
      for (const url of saved.pages) {
        if (!this.isConnected || !await this.load(url, true)) break;
      }
      this.restoring = false;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!this.isConnected) return;
        const wrapper = document.querySelector('.page-wrapper');
        if (wrapper) wrapper.scrollTop = saved.innerY || 0;
        window.scrollTo({ top: saved.y || 0, behavior: 'instant' });
        this.remember();
      }));
    }
  }
  customElements.define('vics-show-more', VicsShowMore);
})();
